import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  ArrowLeft, Plus, Edit, Trash2, Download, FileMinus, Eye, BarChart2, FileText 
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { 
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, 
  BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator 
} from "@/components/ui/breadcrumb";
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from "@/components/ui/table";
import { useRFPData } from "@/hooks/useRFPData";

const Presales = () => {
  const navigate = useNavigate();
  const [newRFP, setNewRFP] = useState({ name: "", description: "" });
  const [isCreating, setIsCreating] = useState(false);
  
  const { rfps, isLoading, fetchRFPs } = useRFPData();

  const createRFP = async () => {
    if (!newRFP.name.trim()) {
      toast({
        title: "Error",
        description: "RFP name is required",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    try {
      const { data, error } = await supabase
        .from('rfps')
        .insert({ 
          name: newRFP.name, 
          description: newRFP.description || null 
        })
        .select();

      if (error) throw error;
      
      if (data && data[0]) {
        navigate(`/rfp/${data[0].id}`);
        toast({
          title: "Success",
          description: "Presales estimation created successfully",
        });
      }
    } catch (error) {
      console.error('Error creating RFP:', error);
      toast({
        title: "Error",
        description: "Failed to create presales estimation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
      setNewRFP({ name: "", description: "" });
    }
  };

  const deleteRFP = async (id: string) => {
    if (!confirm("Are you sure you want to delete this presales estimation? This action cannot be undone.")) {
      return;
    }

    try {
      const { error } = await supabase
        .from('rfps')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      fetchRFPs();
      toast({
        title: "Success",
        description: "Presales estimation deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting RFP:', error);
      toast({
        title: "Error",
        description: "Failed to delete presales estimation. Please try again.",
        variant: "destructive",
      });
    }
  };

  const downloadRFPPDF = async (rfpId: string) => {
    navigate(`/rfp/${rfpId}?tab=report`);
  };

  return (
    <div className="container max-w-5xl py-8 space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-3">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Presales Estimations</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate("/")} 
              className="border-blue-200 dark:border-gray-700 hover:bg-blue-100 dark:hover:bg-gray-700"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
          </div>
        </div>
        <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-700 dark:from-gray-300 dark:to-gray-400 bg-clip-text text-transparent">
          Presales Estimations
        </h1>
      </div>

      <Card className="bg-white dark:bg-gray-900 border-purple-100 dark:border-gray-800">
        <CardHeader>
          <CardTitle className="text-xl text-purple-700 dark:text-gray-300">Create New Presales Estimation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label htmlFor="rfp-name" className="block text-sm font-medium mb-1">RFP Name</label>
              <Input
                id="rfp-name"
                placeholder="Enter RFP name"
                value={newRFP.name}
                onChange={(e) => setNewRFP({ ...newRFP, name: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="rfp-description" className="block text-sm font-medium mb-1">Description (optional)</label>
              <Textarea
                id="rfp-description"
                placeholder="Enter RFP description"
                value={newRFP.description}
                onChange={(e) => setNewRFP({ ...newRFP, description: e.target.value })}
              />
            </div>
            <Button 
              onClick={createRFP} 
              disabled={isCreating} 
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              {isCreating ? "Creating..." : "Create Presales Estimation"} <Plus className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white dark:bg-gray-900 border-purple-100 dark:border-gray-800">
        <CardHeader>
          <CardTitle className="text-xl text-purple-700 dark:text-gray-300">My Presales Estimations</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : rfps.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No presales estimations yet. Create your first one to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-purple-50 dark:bg-gray-800">
                    <TableHead>RFP Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rfps.map((rfp) => (
                    <TableRow key={rfp.id}>
                      <TableCell className="font-medium">{rfp.name}</TableCell>
                      <TableCell className="text-sm text-gray-600 dark:text-gray-400 max-w-[200px] truncate">
                        {rfp.description || "No description"}
                      </TableCell>
                      <TableCell className="text-sm">v{rfp.version}</TableCell>
                      <TableCell className="text-sm">{new Date(rfp.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-purple-600"
                            onClick={() => navigate(`/rfp/${rfp.id}`)}
                            title="View"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-purple-600"
                            onClick={() => navigate(`/rfp/${rfp.id}?tab=estimation`)}
                            title="Edit Estimation"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-green-600"
                            onClick={() => downloadRFPPDF(rfp.id)}
                            title="View Report"
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-purple-600"
                            onClick={() => navigate(`/rfp/${rfp.id}?tab=analytics`)}
                            title="View Analytics"
                          >
                            <BarChart2 className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-red-600"
                            onClick={() => deleteRFP(rfp.id)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
        © {new Date().getFullYear()} <a href="https://cardyai.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">cardyai.com</a>. All rights reserved. | v1.2.0
      </div>
    </div>
  );
};

export default Presales;
